import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core'

const VIEWPORT_MARGIN = 8

// Fixed, not absolute: an `overflow-x: auto` ancestor clips both axes. A fixed menu does not
// follow its trigger, so a scroll or resize dismisses it.
@Directive({
  selector: '[anchoredMenu]',
  standalone: true,
})
export class AnchoredMenuDirective implements AfterViewInit, OnDestroy {
  @Input({ required: true }) anchoredMenu!: HTMLElement
  @Input() gap = 6
  @Output() readonly dismiss = new EventEmitter<void>()

  private resizeObserver?: ResizeObserver
  private readonly emitDismiss = () => this.dismiss.emit()

  constructor(private element: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.place()
    // Scroll does not bubble; only a capture listener on document sees an inner scroller.
    document.addEventListener('scroll', this.emitDismiss, true)
    window.addEventListener('resize', this.emitDismiss)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.place())
      this.resizeObserver.observe(this.element.nativeElement)
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
    document.removeEventListener('scroll', this.emitDismiss, true)
    window.removeEventListener('resize', this.emitDismiss)
  }

  // Not a binding: this runs after the view is checked, so a bound value throws in dev mode.
  private place(): void {
    const menu = this.element.nativeElement
    menu.style.position = 'fixed'
    const anchor = this.anchoredMenu.getBoundingClientRect()
    const height = menu.offsetHeight
    const width = menu.offsetWidth
    const below = anchor.bottom + this.gap
    const above = anchor.top - this.gap - height
    const flip = below + height > window.innerHeight - VIEWPORT_MARGIN && above > VIEWPORT_MARGIN
    const top = flip ? above : Math.min(below, Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - height))
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - width, window.innerWidth - VIEWPORT_MARGIN - width))
    menu.style.top = `${top}px`
    menu.style.left = `${left}px`
  }
}
